import cx from 'classnames';
import { Person } from 'education-icons';
import { Button, Popup, SimpleText, Text, useMe } from 'platform-components';
import { POPUP_DIRECTIONS, POPUP_THEMES } from 'platform-components/constants';
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import ym from 'react-yandex-metrika';

import { accessibleMouseHover } from '@edu-frontend/modules/src/shared/lib/a11y';

import { LIBRARY_CONTENT_TYPE } from 'common.components/constants';
import { useGetLessonRunUrl } from 'common.components/hooks';
import { getLessonViewCourseId } from 'common.components/lesson-view.store/selectors';
import lessonActions from 'common.components/lesson/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';
import withTime, { type InjectedProps as WithNowProps } from 'common.components/timer/withTime';

import * as selectors from '../selectors';

import './lesson-view__look-as-student-button.scss';

const POPUP_WIDTH = 178;

interface ParamTypes {
    subject: string;
}

interface Props extends WithNowProps {
    disabled?: boolean;
    shouldShowTooltip?: boolean;
}

const TOOLTIP_TEXT_ID = 'lessonView.putOnBoard.sandboxWarning';

const LookAsStudentButtonComponent = ({ disabled, now, shouldShowTooltip }: Props) => {
    const { subject } = useParams<ParamTypes>();
    const dispatch = useDispatch();
    const me = useMe();

    const clessonId = useSelector(lessonSelectors.getCLessonId);
    const courseId = useSelector(getLessonViewCourseId);
    const isPutOnBoardAvailable = useSelector((storeState) => selectors.getIsPutOnBoardAvailable(storeState, { now }));
    const isPreparedLesson = useSelector(lessonSelectors.getIsPreparedLesson);
    const activeSubscriptionSlug = useSelector((store) => {
        return lessonSelectors.getLessonActiveSubscriptionSlug(store, { courseId });
    });
    const getLessonRunUrl = useGetLessonRunUrl();

    const popupRef = useRef<Popup>(null);

    const handleClick = async () => {
        let courseLessonLinkId = clessonId;
        if (isPreparedLesson && courseId) {
            courseLessonLinkId = (await dispatch(lessonActions.addLessonToCourseAsync())) as unknown as number;
        }

        const url = getLessonRunUrl({ courseId: courseId!, clessonId: courseLessonLinkId! });

        ym('reachGoal', 'teacher_lesson_creation_preview', {
            subject,
            contentType: isPreparedLesson ? LIBRARY_CONTENT_TYPE.PREPARED_LESSON : LIBRARY_CONTENT_TYPE.COURSE_LESSON,
            type: 'c-menu',
            clessonId: clessonId,
            subscription_slug: activeSubscriptionSlug,
        });

        ym('reachGoal', 'teacher_view_as_student', {
            teacher_id: me?.id,
            clessonId,
        });

        window.location.replace(url);
    };

    const showTooltip = () => {
        if (shouldShowTooltip) {
            popupRef?.current?.show();
        }
    };

    const hideTooltip = () => {
        popupRef?.current?.hide();
    };

    if (!isPutOnBoardAvailable) {
        return null;
    }

    return (
        <div
            className={cx(
                'lesson-view__header-button',
                'lesson-view__look-as-student-control',
                'lesson-view__controll-button'
            )}
            {...accessibleMouseHover({
                onMouseOut: hideTooltip,
                onMouseOver: showTooltip,
                describedby: TOOLTIP_TEXT_ID,
            })}
        >
            <Button
                className="lesson-view__look-as-student-button lesson-view__header-button"
                disabled={disabled}
                icon={<Person />}
                onClick={handleClick}
                view={Button.VIEW.SECONDARY}
            >
                <SimpleText id="customLessonView.showCardsButton" />
            </Button>
            <Popup
                //@ts-ignore TODO: тайпинги для попапа https://st.yandex-team.ru/EDUCATION-11116
                direction={POPUP_DIRECTIONS.TOP}
                mix="lesson-view__put-on-board-popup"
                offsetLeft={POPUP_WIDTH / 2}
                offsetTop={8}
                ref={popupRef}
                theme={POPUP_THEMES.DARK_ROUNDED}
                width={POPUP_WIDTH}
            >
                <Text id={TOOLTIP_TEXT_ID} size={Text.SIZE.S} />
            </Popup>
        </div>
    );
};

const lSButton = withTime(LookAsStudentButtonComponent);

lSButton.displayName = 'LookAsStudentButton';

export const LookAsStudentButton = lSButton;
